/**
 * Hook para gerenciamento de logs e estatísticas de webhooks de flows
 * 
 * Fornece funções para buscar logs paginados, estatísticas,
 * volume por hora, regenerar token e testar webhooks.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { getEdgeFunctionUrl } from '@/lib/supabaseHelpers';
import { toast } from 'sonner';

// Logger para o hook
const logger = createLogger('useFlowWebhooks');

/**
 * Interface de log de webhook de flow
 */
export interface FlowWebhookLog {
  id: string;
  flow_id: string;
  tenant_id: string;
  mode: 'conversation' | 'deal';
  status: 'success' | 'error' | 'validation_error' | 'auth_error' | 'duplicate';
  error_message: string | null;
  error_code: string | null;
  contact_phone: string | null;
  contact_id: string | null;
  conversation_id: string | null;
  deal_id: string | null;
  execution_id: string | null;
  request_payload: Record<string, unknown> | null;
  request_ip: string | null;
  processing_time_ms: number | null;
  created_at: string;
}

/**
 * Interface de estatísticas de webhook de flow
 */
export interface FlowWebhookStats {
  totalRequests: number;
  successfulRequests: number;
  failedRequests: number;
  successRate: number;
  lastRequestAt: string | null;
  avgProcessingTime: number;
}

/**
 * Interface de volume por hora
 */
export interface FlowWebhookVolumeData {
  hora: number;
  data_hora: string;
  total: number;
  sucesso: number;
  erro: number;
}

/**
 * Resultado paginado de logs
 */
export interface PaginatedFlowLogsResult {
  logs: FlowWebhookLog[];
  totalCount: number;
}

/**
 * Interface de retorno do hook
 */
export interface UseFlowWebhooksReturn {
  // Logs
  logs: FlowWebhookLog[];
  isLoadingLogs: boolean;
  logsTotal: number;
  fetchLogs: (
    flowId: string,
    page: number,
    pageSize: number,
    filter?: string
  ) => Promise<PaginatedFlowLogsResult>;

  // Estatísticas
  stats: FlowWebhookStats | null;
  isLoadingStats: boolean;
  fetchStats: (flowId: string) => Promise<void>;

  // Volume para gráfico
  volumeData: FlowWebhookVolumeData[];
  isLoadingVolume: boolean;
  fetchVolume: (flowId: string, hours?: number) => Promise<void>;

  // Token
  regenerateToken: (flowId: string) => Promise<string | null>;
  isRegenerating: boolean;

  // Teste
  testWebhook: (
    webhookToken: string,
    payload: Record<string, unknown>
  ) => Promise<{
    success: boolean;
    status: number;
    data: unknown;
    time: number;
  }>;
  isTesting: boolean;
}

/**
 * Hook principal para gerenciamento de webhooks de flows
 */
export function useFlowWebhooks(): UseFlowWebhooksReturn {
  // Estados de logs
  const [logs, setLogs] = useState<FlowWebhookLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsTotal, setLogsTotal] = useState(0);

  // Estados de estatísticas
  const [stats, setStats] = useState<FlowWebhookStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Estados de volume
  const [volumeData, setVolumeData] = useState<FlowWebhookVolumeData[]>([]);
  const [isLoadingVolume, setIsLoadingVolume] = useState(false);

  // Estados de token
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Estado de teste
  const [isTesting, setIsTesting] = useState(false);

  /**
   * Busca logs paginados do webhook
   */
  const fetchLogs = useCallback(
    async (
      flowId: string,
      page: number,
      pageSize: number,
      filter?: string
    ): Promise<PaginatedFlowLogsResult> => {
      setIsLoadingLogs(true);

      try {
        const offset = (page - 1) * pageSize;
        logger.debug('Buscando logs de flow webhook', { flowId, page, pageSize, filter });

        // Buscar logs com count
        let query = supabase
          .from('flow_webhook_logs')
          .select('*', { count: 'exact' })
          .eq('flow_id', flowId)
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);

        // Aplicar filtro de status
        if (filter && filter !== 'all') {
          query = query.eq('status', filter);
        }

        const { data, error, count } = await query;

        if (error) {
          logger.error('Erro ao buscar logs', error);
          throw error;
        }

        const result = {
          logs: (data || []) as FlowWebhookLog[],
          totalCount: count || 0,
        };

        setLogs(result.logs);
        setLogsTotal(result.totalCount);

        return result;
      } catch (error) {
        logger.error('Erro ao buscar logs', error);
        setLogs([]);
        setLogsTotal(0);
        return { logs: [], totalCount: 0 };
      } finally {
        setIsLoadingLogs(false);
      }
    },
    []
  );

  /**
   * Busca estatísticas do webhook
   */
  const fetchStats = useCallback(async (flowId: string): Promise<void> => {
    setIsLoadingStats(true);

    try {
      logger.debug('Buscando estatísticas do flow webhook', flowId);

      // Buscar dados do flow
      const { data: flow, error: flowError } = await supabase
        .from('flows')
        .select(
          'webhook_total_requests, webhook_successful_requests, webhook_failed_requests, webhook_last_request_at'
        )
        .eq('id', flowId)
        .single();

      if (flowError) throw flowError;

      // Buscar tempo médio de processamento dos logs
      const { data: avgData, error: avgError } = await supabase
        .from('flow_webhook_logs')
        .select('processing_time_ms')
        .eq('flow_id', flowId)
        .limit(100);

      if (avgError) throw avgError;

      const avgProcessingTime =
        avgData && avgData.length > 0
          ? avgData.reduce((acc, log) => acc + (log.processing_time_ms || 0), 0) / avgData.length
          : 0;

      const totalRequests = flow?.webhook_total_requests || 0;
      const successfulRequests = flow?.webhook_successful_requests || 0;

      setStats({
        totalRequests,
        successfulRequests,
        failedRequests: flow?.webhook_failed_requests || 0,
        successRate: totalRequests > 0 ? (successfulRequests / totalRequests) * 100 : 0,
        lastRequestAt: flow?.webhook_last_request_at || null,
        avgProcessingTime: Math.round(avgProcessingTime),
      });
    } catch (error) {
      logger.error('Erro ao buscar estatísticas', error);
      setStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  }, []);

  /**
   * Busca volume por hora para gráfico
   */
  const fetchVolume = useCallback(
    async (flowId: string, hours: number = 24): Promise<void> => {
      setIsLoadingVolume(true);

      try {
        logger.debug('Buscando volume por hora', { flowId, hours });

        // Calcular data de início
        const startDate = new Date();
        startDate.setHours(startDate.getHours() - hours);

        // Buscar logs agrupados por hora
        const { data, error } = await supabase
          .from('flow_webhook_logs')
          .select('created_at, status')
          .eq('flow_id', flowId)
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true });

        if (error) throw error;

        // Agrupar por hora
        const hourMap = new Map<number, { total: number; sucesso: number; erro: number }>();

        // Inicializar todas as horas com zero
        for (let i = 0; i < hours; i++) {
          const date = new Date();
          date.setHours(date.getHours() - (hours - 1 - i));
          const hour = date.getHours();
          hourMap.set(hour, { total: 0, sucesso: 0, erro: 0 });
        }

        // Preencher com dados reais
        data?.forEach((log) => {
          const logDate = new Date(log.created_at);
          const hour = logDate.getHours();
          const current = hourMap.get(hour) || { total: 0, sucesso: 0, erro: 0 };
          current.total++;
          if (log.status === 'success') {
            current.sucesso++;
          } else {
            current.erro++;
          }
          hourMap.set(hour, current);
        });

        // Converter para array ordenado
        const volumeArray: FlowWebhookVolumeData[] = [];
        const now = new Date();

        for (let i = 0; i < hours; i++) {
          const date = new Date();
          date.setHours(now.getHours() - (hours - 1 - i));
          const hour = date.getHours();
          const hourData = hourMap.get(hour) || { total: 0, sucesso: 0, erro: 0 };

          volumeArray.push({
            hora: hour,
            data_hora: date.toISOString(),
            total: hourData.total,
            sucesso: hourData.sucesso,
            erro: hourData.erro,
          });
        }

        setVolumeData(volumeArray);
      } catch (error) {
        logger.error('Erro ao buscar volume', error);
        setVolumeData([]);
      } finally {
        setIsLoadingVolume(false);
      }
    },
    []
  );

  /**
   * Regenera o token do webhook
   */
  const regenerateToken = useCallback(
    async (flowId: string): Promise<string | null> => {
      setIsRegenerating(true);

      try {
        logger.info('Regenerando token do webhook', flowId);

        const { data, error } = await supabase.rpc('regenerate_flow_webhook_token', {
          p_flow_id: flowId,
        });

        if (error) {
          logger.error('Erro ao regenerar token', error);
          toast.error('Erro ao regenerar token');
          return null;
        }

        toast.success('Token regenerado com sucesso');
        return data as string;
      } catch (error) {
        logger.error('Erro ao regenerar token', error);
        toast.error('Erro ao regenerar token');
        return null;
      } finally {
        setIsRegenerating(false);
      }
    },
    []
  );

  /**
   * Testa o webhook com um payload
   */
  const testWebhook = useCallback(
    async (
      webhookToken: string,
      payload: Record<string, unknown>
    ): Promise<{
      success: boolean;
      status: number;
      data: unknown;
      time: number;
    }> => {
      setIsTesting(true);

      try {
        logger.info('Testando webhook de flow', webhookToken.substring(0, 10));

        // Construir URL usando helper centralizado
        // Encode do token para preservar caracteres especiais (+, =, etc.)
        const webhookUrl = `${getEdgeFunctionUrl('flow-webhook')}?token=${encodeURIComponent(webhookToken)}`;

        const startTime = performance.now();

        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const endTime = performance.now();
        const time = Math.round(endTime - startTime);

        const data = await response.json();

        const result = {
          success: response.ok,
          status: response.status,
          data,
          time,
        };

        if (result.success) {
          toast.success(`Teste bem-sucedido em ${time}ms`);
        } else {
          toast.error(`Erro no teste: ${data.error || 'Erro desconhecido'}`);
        }

        return result;
      } catch (error) {
        logger.error('Erro ao testar webhook', error);
        toast.error('Erro ao conectar com o webhook');
        return {
          success: false,
          status: 0,
          data: { error: 'Erro de conexão' },
          time: 0,
        };
      } finally {
        setIsTesting(false);
      }
    },
    []
  );

  return {
    // Logs
    logs,
    isLoadingLogs,
    logsTotal,
    fetchLogs,

    // Estatísticas
    stats,
    isLoadingStats,
    fetchStats,

    // Volume
    volumeData,
    isLoadingVolume,
    fetchVolume,

    // Token
    regenerateToken,
    isRegenerating,

    // Teste
    testWebhook,
    isTesting,
  };
}
